import React, { useEffect, useState } from "react";
import BaseLayout from "../../components/Layout";
import { useSelector } from "react-redux";
import { actionListUsers } from "../../features/usuarios-list/slices";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector"; // ou seu hook de selector tipado
import Button from "../../components/Button";

import "./styles.scss";
import { mapNiveisDeAcesso } from "../../helpers/factory-fields";
import Modal from "../../components/Modal";
import RegisterUser from "./forms/RegisterUser";
import { actionDeleteUser } from "../../features/usuarios-register/slices";
import { toast } from "sonner";
import { actionToggleUserStatus } from "../../features/usuarios-alterarStatus/slices";
import { getUser } from "../../helpers/user";

const CadastroUser = () => {
  const dispatch = useAppDispatch();
  const users = useSelector((state: any) => state.listUsuarios.data);
  const status = useSelector((state: any) => state.listUsuarios.status);

  // Busca as listas de regionais e lojas do Redux
  const regionais = useAppSelector(
    (state: any) => state.getRegionais.regionais,
  );
  const lojas = useAppSelector((state: any) => state.getLojas.lojas);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const loggedUser = getUser();
  const userNivel = Number(loggedUser.usuario?.nivel || 0);
  const NIVEL_COORDENADOR_REGIONAL = 9;
const NIVEL_TECNICO = 999;
  const isHighLevel = userNivel >= NIVEL_TECNICO;

  useEffect(() => {
    dispatch(actionListUsers());
  }, [dispatch]);

  const filteredUsers = isHighLevel
    ? users
    : users?.filter((u: any) => Number(u.nivel) !== 4);

  const handleOpenModal = (user: any = null) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleToggleActive = (user: any) => {
    const action = user.ativo === 1 ? "desativar" : "ativar";
    if (window.confirm(`Deseja realmente ${action} o usuário ${user.nome}?`)) {
      dispatch(
        actionToggleUserStatus({
          id: user.id,
          ativo: user.ativo === 1 ? false : true,
        }),
      );
      toast.success(
        `Usuário ${action === "ativar" ? "ativado" : "desativado"} com sucesso`,
      );
    }
  };

  const handleDelete = async (id: number) => {
    if (
      window.confirm("Deseja realmente EXCLUIR permanentemente este usuário?")
    ) {
      try {
        await dispatch(actionDeleteUser(id)).unwrap();
        dispatch(actionListUsers());
        toast.success("Usuário excluído com sucesso");
      } catch (e) {
        toast.error("Erro ao excluir usuário");
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleSaveSuccess = () => {
    handleCloseModal();
    dispatch(actionListUsers());
  };

  const renderLojaRegional = (user: any) => {
    const nivel = Number(user.nivel || 0);
    const idLoja = Number(user.idLoja || user.loja?.id || 0);
    const idRegional = Number(user.idRegional || 0);

    // NOVO: Coordenador Regional (vínculo N:N) — precisa vir ANTES da regra "nivel > 6"
    if (nivel === NIVEL_COORDENADOR_REGIONAL) {
      const ids: number[] = user.idsRegionaisVinculadas || [];

      if (ids.length === 0) {
        return <span className="text-muted">Nenhuma regional vinculada</span>;
      }

      return (
        <div className="coordenador-regionais">
          {ids.map((idReg) => {
            const regional = regionais?.find(
              (r: any) => Number(r.id) === idReg,
            );
            const lojasDaRegional =
              lojas?.filter((l: any) => Number(l.idRegional) === idReg) ?? [];

            return (
              <span
                key={idReg}
                className="badge-nivel"
                style={{ marginRight: 4, display: "inline-block" }}
                title={
                  lojasDaRegional.length > 0
                    ? lojasDaRegional.map((l: any) => l.nome).join(", ")
                    : "Nenhuma loja encontrada nesta regional"
                }
              >
                {regional?.nome || `Regional ${idReg}`}
                {lojasDaRegional.length > 0 && ` (${lojasDaRegional.length})`}
              </span>
            );
          })}
        </div>
      );
    }

    // Regra 1: Nível superior (> 6) tem acesso geral
    if (nivel > 6) {
      return <span className="text-muted">Acesso Geral</span>;
    }

    // Regra 2: Possui Loja (procura no redux ou usa o objeto do usuário)
    if (idLoja > 0) {
      const lojaEncontrada = lojas?.find((l: any) => Number(l.id) === idLoja);
      const nomeLoja = lojaEncontrada?.nome || user.loja?.nome;
      const numeroLoja = lojaEncontrada?.numero || user.loja?.numero;

      if (nomeLoja) {
        return (
          <>
            {numeroLoja && <strong>{numeroLoja} - </strong>}
            {nomeLoja}
          </>
        );
      }
    }

    // Regra 3: Sem Loja (idLoja === 0), mas vinculado a uma Regional
    if (idRegional > 0) {
      const regionalEncontrada = regionais?.find(
        (r: any) => Number(r.id) === idRegional,
      );
      const nomeRegional = regionalEncontrada?.nome || user.nomeRegional;

      return (
        <span>
          <strong>Regional:</strong> {nomeRegional || `Regional ${idRegional}`}
        </span>
      );
    }

    // Fallback padrão
    return <span className="text-muted">Acesso Geral</span>;
  };

  return (
    <BaseLayout>
      <div className="users-management-container">
        <header className="page-header">
          <div>
            <h1>Gerenciamento de Usuários</h1>
            <p>Visualize e gerencie os acessos ao sistema.</p>
          </div>
          <Button variant="primary" size="md" onClick={() => handleOpenModal()}>
            Novo Usuário
          </Button>
        </header>

        <div className="users-card">
          {status === "loading" ? (
            <div className="loading-state">Carregando usuários...</div>
          ) : (
            <div className="table-responsive">
              <table className="users-table">
                <thead>
                  <tr>
                    <th className="col-status">Status</th>
                    <th>Nome / Login</th>

                    {isHighLevel && <th>E-mail</th>}
                    <th>Nível</th>
                    {isHighLevel && <th>Chave</th>}
                    <th>Loja / Regional</th>
                    <th className="col-actions">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers?.map((user: any) => (
                    <tr key={user.id}>
                      <td className="text-center">
                        <div className="status-indicator-wrapper">
                          {user.reset === 1 ? (
                            <span
                              className="dot pulse-orange"
                              title="Aguardando Reset de Senha"
                            ></span>
                          ) : user.ativo === 1 ? (
                            <span
                              className="dot pulse-green"
                              title="Usuário Ativo"
                            ></span>
                          ) : (
                            <span
                              className="dot dot-gray"
                              title="Usuário Inativo"
                            ></span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="user-info">
                          <span className="user-name">{user.nome}</span>
                          <span className="user-login">@{user.login}</span>
                        </div>
                      </td>

                      {isHighLevel && <td>{user.email}</td>}

                      <td>
                        <span className="badge-nivel">
                          {mapNiveisDeAcesso[user.nivel] || "Não definido"}
                        </span>
                      </td>

                      {isHighLevel && <td>{user.chave || "—"}</td>}

                      <td>
                        <div className="store-info">
                          {renderLojaRegional(user)}
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-action edit"
                            title="Editar"
                            onClick={() => handleOpenModal(user)}
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </button>
                          <button
                            className={`btn-action ${
                              user.ativo === 1 ? "deactivate" : "activate"
                            }`}
                            title={user.ativo === 1 ? "Desativar" : "Ativar"}
                            onClick={() => handleToggleActive(user)}
                          >
                            <i
                              className={`fa-solid ${
                                user.ativo === 1
                                  ? "fa-user-slash"
                                  : "fa-user-check"
                              }`}
                            ></i>
                          </button>
                          <button
                            className="btn-action delete"
                            title="Excluir"
                            onClick={() => handleDelete(user.id)}
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {isModalOpen && (
        <Modal
          header={
            <h3>
              {selectedUser ? "Editar Usuário" : "Cadastrar Novo Usuário"}
            </h3>
          }
          callbackCancel={handleCloseModal}
          size="lg"
          overflow={true}
        >
          <RegisterUser
            user={selectedUser}
            callback={handleSaveSuccess}
            cancelAction={handleCloseModal}
          />
        </Modal>
      )}
    </BaseLayout>
  );
};

export default CadastroUser;
